import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { format } from "date-fns";
import Layout from "../layouts/layout";
import Loader from "../components/loader";
import Spinner from "../components/spinner";
import PAYMENT_REQUESTS from "../api/services/PAYMENT_REQUESTS";
import PaymentRequestDetail from "../containers/_paymentRequests/paymentRequestDetail";
import styles from "../styles/_screens/paymentRequests.module.scss";

const STATUS_TABS = [
  { key: "ALL", label: "הכל" },
  { key: "PENDING", label: "ממתין" },
  { key: "PAID", label: "שולם" },
  { key: "EXPIRED", label: "פג תוקף" },
  { key: "CANCELLED", label: "בוטל" },
];

const STATUS_LABELS = {
  PENDING: "ממתין",
  PAID: "שולם",
  EXPIRED: "פג תוקף",
  CANCELLED: "בוטל",
};

const PAGE_SIZE = 20;

export default function PaymentRequests() {
  const [requests, setRequests] = useState([]);
  const [status, setStatus] = useState("ALL");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const debounceRef = useRef(null);

  // ── Fetch payment requests ──
  const fetchRequests = useCallback(async (currentPage, currentStatus, currentSearch, append = false) => {
    if (currentPage === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const res = await PAYMENT_REQUESTS.list({
        status: currentStatus,
        page: currentPage,
        size: PAGE_SIZE,
        search: currentSearch,
      });
      const data = res?.data;
      const items = Array.isArray(data) ? data : (data?.items || data?.requests || data?.data || []);
      const total = data?.total;

      if (append) {
        setRequests((prev) => [...prev, ...items]);
      } else {
        setRequests(items);
      }

      if (total != null) {
        setHasMore(currentPage * PAGE_SIZE < total);
      } else {
        setHasMore(items.length >= PAGE_SIZE);
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "שגיאה בטעינת בקשות תשלום");
      if (!append) setRequests([]);
    }
    setLoading(false);
    setLoadingMore(false);
  }, []);

  // Trigger fetch on status / page / search changes
  useEffect(() => {
    if (page === 1) {
      fetchRequests(1, status, search, false);
    } else {
      fetchRequests(page, status, search, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, page]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchRequests(1, status, search, false);
    }, 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // ── Status change handler ──
  const handleStatusChange = (newStatus) => {
    if (newStatus === status) return;
    setStatus(newStatus);
    setPage(1);
  };

  // ── Load more ──
  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
  };

  // ── Cancel request ──
  const handleCancel = async (e, id) => {
    e.stopPropagation();
    setActionLoading(id + "_cancel");
    try {
      await PAYMENT_REQUESTS.cancel(id);
      toast.success("הבקשה בוטלה בהצלחה");
      refetchAll();
    } catch (error) {
      toast.error(error?.response?.data?.message || "שגיאה בביטול הבקשה");
    }
    setActionLoading(null);
  };

  // ── Resend request ──
  const handleResend = async (e, id, channel) => {
    e.stopPropagation();
    setActionLoading(id + "_resend_" + channel);
    try {
      await PAYMENT_REQUESTS.resend(id, channel);
      toast.success("הבקשה נשלחה מחדש בהצלחה");
    } catch (error) {
      toast.error(error?.response?.data?.message || "שגיאה בשליחה מחדש");
    }
    setActionLoading(null);
  };

  // ── Refetch current view (after panel actions) ──
  const refetchAll = () => {
    setPage(1);
    fetchRequests(1, status, search, false);
  };

  // ── Panel action callback ──
  const handlePanelAction = () => {
    setSelectedRequest(null);
    refetchAll();
  };

  // ── Status pill class ──
  const getStatusClass = (s) => {
    switch (s) {
      case "PENDING": return styles.statusPending;
      case "PAID": return styles.statusPaid;
      case "EXPIRED": return styles.statusExpired;
      case "CANCELLED": return styles.statusCancelled;
      default: return "";
    }
  };

  // ── Loading state ──
  if (loading && requests.length === 0) {
    return (
      <Layout>
        <div className={styles.container}>
          <div className={styles.wrapper}>
            <div className={styles.main}>
              <Loader />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div className={styles.main}>
            <div className={styles.tableContainer}>
              {/* Header */}
              <div className={styles.header}>
                <h2>בקשות תשלום</h2>
                {/* Future: create button can go here */}
                {/* <button className={styles.createBtn}>צור בקשת תשלום +</button> */}
              </div>

              {/* Status tabs */}
              <div className={styles.tabs}>
                {STATUS_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    className={`${styles.tab} ${status === tab.key ? styles.tabActive : ""}`}
                    onClick={() => handleStatusChange(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className={styles.searchRow}>
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="חיפוש לפי שם לקוח..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  dir="rtl"
                />
              </div>

              {/* Table */}
              {requests.length === 0 ? (
                <div className={styles.emptyState}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  <p>אין בקשות תשלום</p>
                </div>
              ) : (
                <div className={styles.tableWrapper}>
                  <table>
                    <thead>
                      <tr>
                        <th>לקוח</th>
                        <th>סכום</th>
                        <th>שירות</th>
                        <th>תאריך שליחה</th>
                        <th>סטטוס</th>
                        <th>פעולות</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((req) => (
                        <tr
                          key={req._id}
                          className={styles.tableRow}
                          onClick={() => setSelectedRequest(req)}
                        >
                          <td>
                            <div className={styles.customerCell}>
                              <span className={styles.customerName}>
                                {req.customerName || req.customer?.name || "-"}
                              </span>
                              <span className={styles.customerPhone}>
                                {req.customerPhone || req.customer?.phone || ""}
                              </span>
                            </div>
                          </td>
                          <td>{"\u20AA"}{req.amount}</td>
                          <td>{req.serviceName || req.service?.name || "-"}</td>
                          <td>
                            {req.createdAt
                              ? format(new Date(req.createdAt), "dd/MM/yyyy HH:mm")
                              : "-"}
                          </td>
                          <td>
                            <span className={`${styles.statusBadge} ${getStatusClass(req.status)}`}>
                              {STATUS_LABELS[req.status] || req.status}
                            </span>
                          </td>
                          <td>
                            {req.status === "PENDING" && (
                              <div className={styles.actions}>
                                <button
                                  className={styles.resendBtn}
                                  onClick={(e) => handleResend(e, req._id, "whatsapp")}
                                  disabled={actionLoading === req._id + "_resend_whatsapp"}
                                >
                                  {actionLoading === req._id + "_resend_whatsapp" ? (
                                    <Spinner />
                                  ) : (
                                    "שלח שוב"
                                  )}
                                </button>
                                <button
                                  className={styles.cancelBtn}
                                  onClick={(e) => handleCancel(e, req._id)}
                                  disabled={actionLoading === req._id + "_cancel"}
                                >
                                  {actionLoading === req._id + "_cancel" ? (
                                    <Spinner />
                                  ) : (
                                    "בטל"
                                  )}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Load more */}
              {hasMore && requests.length > 0 && (
                <div className={styles.loadMore}>
                  <button
                    className={styles.loadMoreBtn}
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? <Spinner /> : "טען עוד"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Detail side panel */}
      {selectedRequest && (
        <PaymentRequestDetail
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onAction={handlePanelAction}
        />
      )}
    </Layout>
  );
}
